import {
  PRODUCT_DASHBOARD_SEARCH_RESULTS_LOADED,
  PRODUCT_DASHBOARD_SEARCH_RESULTS_CLEAR,
  PRODUCT_DASHBOARD_SEARCH_RESULTS_META_LOADED,
  PRODUCT_DASHBOARD_SEARCH_RESULTS_META_CLEAR,
} from '../../actionTypes';

import * as api from '../../apiHelper';
import {
  setFetchLoaderLoading,
  setFetchLoaderReset,
  setSubmissionLoaderLoading,
  setSubmissionLoaderReset,
} from '../../fetchLoaderActions';
import { arriveNotice } from '../../noticeActions';
import { setFaviconLoading, setFaviconDefault } from '../../faviconActions';

// Reducer corresponding actions
function dashboardProductSearchResultsClear() {
  return { type: PRODUCT_DASHBOARD_SEARCH_RESULTS_CLEAR };
}

function dashboardProductSearchResultsLoaded(result) {
  return {
    type: PRODUCT_DASHBOARD_SEARCH_RESULTS_LOADED,
    payload: { result },
  };
}

function dashboardProductSearchResultsMetaClear() {
  return { type: PRODUCT_DASHBOARD_SEARCH_RESULTS_META_CLEAR };
}

function dashboardProductSearchResultsMetaLoaded(result) {
  return {
    type: PRODUCT_DASHBOARD_SEARCH_RESULTS_META_LOADED,
    payload: { result },
  };
}

// Helper: Map Rails query params to Search Service format for search by ID
function mapSearchByIdParams(railsParams) {
  /* eslint-disable camelcase */
  const mapped = {};

  // Pagination for results within the search
  if (railsParams.paging) {
    mapped.page = railsParams.paging.to || 1;
    mapped.page_size = railsParams.paging.size || 10;
  }

  return mapped;
  /* eslint-enable camelcase */
}

// API
async function getSearchResultsDataApi(id, args, token, isCSV) {
  if (isCSV) {
    // CSV export not yet supported in Search Service - keep using Rails
    await api.getCSV({
      path: `searches/${id}`,
      args,
      token,
      version: 'v3',
      filename: `redflag-search-${id}-export`,
    });
    return null;
  }

  // Map Rails params to Search Service format
  const searchServiceParams = mapSearchByIdParams(args);

  // Use api.get for flat params
  const response = await api.get({
    path: `searches/${id}`,
    params: searchServiceParams,
    token,
    version: 'gateway',
  });
  const searchServiceData = await response.json();

  // Search Service response structure matches Rails - no mapping needed!
  return searchServiceData;
}

async function getSearchResultsMetaApi(id, args, token) {
  const response = await api.get({
    path: `searches/${id}/meta`,
    params: args,
    token,
    version: 'gateway',
  });
  return response.json();
}

// Helper
async function fetchProductSearchResults(dispatch, id, args, token, isCSV) {
  try {
    const response = await getSearchResultsDataApi(id, args, token, isCSV);

    if (!isCSV) {
      dispatch(setFaviconDefault());
      dispatch(setFetchLoaderReset());
      dispatch(dashboardProductSearchResultsLoaded(response));
    }

    if (isCSV) dispatch(setSubmissionLoaderReset());
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(setFaviconDefault());
      dispatch(arriveNotice(e.message));
      if (isCSV) dispatch(setSubmissionLoaderReset());
    }
  }
}

async function fetchProductSearchResultsMeta(dispatch, id, args, token) {
  try {
    const response = await getSearchResultsMetaApi(id, args, token);

    dispatch(setFaviconDefault());
    dispatch(dashboardProductSearchResultsMetaLoaded(response));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(setFaviconDefault());
      dispatch(arriveNotice(e.message));
    }
  }
}

// Public components actions
export function clearProductDashboardSearchResultsRequest() {
  return (dispatch) => {
    dispatch(dashboardProductSearchResultsClear());
  };
}

export function initiateProductDashboardSearchResultsRequest(id, args, isCSV = false) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      favicon: { faviconStatus },
      fetchLoader: { dataLoading, submissionLoading },
    } = getState();

    if (!dataLoading && !isCSV) {
      dispatch(setFetchLoaderLoading());
    } else if (!submissionLoading && isCSV) {
      dispatch(setSubmissionLoaderLoading());
    }

    if (faviconStatus !== 'loading' && !isCSV) {
      dispatch(setFaviconLoading());
    }

    await fetchProductSearchResults(dispatch, id, args, token, isCSV);
  };
}

export function clearProductDashboardSearchResultsMetaRequest() {
  return (dispatch) => {
    dispatch(dashboardProductSearchResultsMetaClear());
  };
}

export function initiateProductDashboardSearchResultsMetaRequest(id, args) {
  return async (dispatch, getState) => {
    const { auth: { token }, favicon: { faviconStatus } } = getState();

    if (faviconStatus !== 'loading') {
      dispatch(setFaviconLoading());
    }

    await fetchProductSearchResultsMeta(dispatch, id, args, token);
  };
}
